import { Suspense } from 'react';
import { Navigate, RouteObject, useLocation } from 'react-router-dom';
import accountSettingsRouteConfig from '@/accountSettings/Router';
import { enterpriseBasename } from '@/constants';
import App from '@/enterprise/App';
import BuyerCards from '@/enterprise/components/BuyerCards';
import BuyerInvoiceLayout from '@/enterprise/components/BuyerInvoiceLayout';
import BuyerNavigationLayout from '@/enterprise/components/BuyerNavigationLayout';
import ConsolidatedHistoryLayout from '@/enterprise/components/ConsolidatedHistoryLayout';
import InvoiceCentralAdjustments from '@/enterprise/components/InvoiceCentral/Adjustment/Adjustments';
import InvoiceCentralEligibleInvoices from '@/enterprise/components/InvoiceCentral/Eligible/EligibleInvoices';
import InvoiceCentralIneligibleInvoices from '@/enterprise/components/InvoiceCentral/Ineligible/IneligibleInvoices';
import InvoiceCentralLayout from '@/enterprise/components/InvoiceCentral/InvoiceCentralLayout';
import MarketHistoryLayout from '@/enterprise/components/MarketHistoryLayout';
import MarketLayout from '@/enterprise/components/MarketRedirect';
import CustomerSelect from '@/enterprise/components/OfferBuilder/CustomerSelect';
import FrequencySelect from '@/enterprise/components/OfferBuilder/FrequencySelect';
import OfferConfirmationLayout from '@/enterprise/components/OfferBuilder/OfferConfirmation/OfferConfirmationLayout';
import OfferReviewLayout from '@/enterprise/components/OfferBuilder/OfferReview/OfferReviewLayout';
import { PreferredRateStep } from '@/enterprise/components/OfferBuilder/PreferredRateStep/PreferredRateStep';
import RateSelectLayout from '@/enterprise/components/OfferBuilder/RateSelect/RateSelectLayout';
import useEnableUnification from '@/enterprise/components/useEnableUnification';
import AcquisitionOfferReview from '@/enterprise/features/acquisition/AcquisitionOfferReview';
import DraftOfferReview from '@/enterprise/features/draftOffer/reviewLayout/DraftOfferReview';
import HomepageLayout from '@/enterprise/features/homepage/HomepageLayout';
import FixedRateMarketTable from '@/enterprise/features/homepage/marketTable/FixedRateMarketTable';
import InactiveMarketTable from '@/enterprise/features/homepage/marketTable/InactiveMarketTable';
import NameYourRateMarketTable from '@/enterprise/features/homepage/marketTable/NameYourRateMarketTable';
import PreferredRateMarketTable from '@/enterprise/features/homepage/marketTable/PreferredRateMarketTable';
import VariableRateMarketTable from '@/enterprise/features/homepage/marketTable/VariableRateMarketTable';
import TakerMarketTabs from '@/enterprise/features/homepage/TakerMarketTabs';
import Automation from '@/enterprise/pages/Automation';
import AwardedInvoices from '@/enterprise/pages/AwardedInvoices';
import ConsolidatedHistory from '@/enterprise/pages/ConsolidatedHistory';
import EligibleInvoices from '@/enterprise/pages/EligibleInvoices';
import Fundbox from '@/enterprise/pages/Fundbox';
import Homepage from '@/enterprise/pages/Homepage';
import IneligibleInvoices from '@/enterprise/pages/IneligibleInvoices';
import InvoiceAdjustments from '@/enterprise/pages/InvoiceAdjustments';
import Lending from '@/enterprise/pages/Lending';
import MarketHistory from '@/enterprise/pages/MarketHistory';
import OfferActivity from '@/enterprise/pages/OfferActivity';
import OfferBuilder from '@/enterprise/pages/OfferBuilder';
import findYourCustomerRouteConfig from '@/findYourCustomer/Router';
import { FeatureFlagProtectedRoute } from '@/shared/components/FeatureFlagProtectedRouting';
import FullPageLoader from '@/shared/components/FullPageLoader';
import NavigateWithRedirect from '@/shared/components/NavigateWithRedirect';
import { asEnterpriseLink } from '@/utils/experienceLinkHelper';
import { filterAllowedParams } from '@/utils/filterAllowedParams';

export const EnterpriseRedirect = () => {
  const location = useLocation();
  const filteredSearch = filterAllowedParams(location.search);
  const enableUnification = useEnableUnification();

  const route = enableUnification
    ? `/${enterpriseBasename}/homepage${filteredSearch}`
    : `/${enterpriseBasename}/customers${filteredSearch}`;

  return <Navigate to={route} />;
};

const enterpriseRouteConfig: RouteObject = {
  path: enterpriseBasename,
  element: (
    <Suspense fallback={<FullPageLoader />}>
      <App />
    </Suspense>
  ),
  children: [
    { index: true, element: <EnterpriseRedirect /> },
    {
      element: <HomepageLayout />,
      children: [
        {
          path: 'markets',
          children: [
            {
              element: <TakerMarketTabs />,
              children: [
                {
                  index: true,
                  element: <NameYourRateMarketTable />,
                },
                {
                  path: 'inactive',
                  element: <InactiveMarketTable />,
                },
                {
                  path: 'fixed',
                  element: <FixedRateMarketTable />,
                },
                {
                  path: 'preferred',
                  element: <PreferredRateMarketTable />,
                },
                {
                  path: 'variable',
                  element: <VariableRateMarketTable />,
                },
                {
                  // catch all route for /markets/:route that does not exist
                  path: '*',
                  element: <NavigateWithRedirect to="/markets" />,
                },
              ],
            },
          ],
        },
        {
          path: 'customers',
          element: <BuyerCards />,
        },
      ],
    },
    {
      path: 'homepage',
      element: <Homepage />,
    },
    {
      path: 'offer-activity',
      element: <OfferActivity />,
    },
    {
      path: 'invoice-central',
      children: [
        {
          index: true,
          element: <InvoiceCentralLayout />,
        },
        {
          path: ':makerOrganizationUuid/:currency',
          element: <InvoiceCentralLayout />,
          children: [
            {
              index: true,
              element: <NavigateWithRedirect to="eligible" />,
            },
            {
              path: 'eligible',
              element: <InvoiceCentralEligibleInvoices />,
            },
            {
              path: 'ineligible',
              element: <InvoiceCentralIneligibleInvoices />,
            },
            {
              path: 'adjustments',
              element: <InvoiceCentralAdjustments />,
            },
          ],
        },
      ],
    },
    {
      path: 'customers/:makerOrganizationUuid',
      element: <BuyerNavigationLayout />,
      children: [
        {
          path: 'markets/:marketId/division/:takerId',
          children: [
            {
              index: true,
              element: <NavigateWithRedirect to="invoices/eligible" />,
            },
            {
              path: 'invoices',
              element: <BuyerInvoiceLayout />,
              children: [
                {
                  index: true,
                  element: <NavigateWithRedirect to="eligible" />,
                },
                {
                  path: 'adjustments',
                  element: <InvoiceAdjustments />,
                },
                {
                  path: 'awarded',
                  element: <AwardedInvoices />,
                },
                {
                  path: 'eligible',
                  element: <EligibleInvoices />,
                },
                {
                  path: 'ineligible',
                  element: <IneligibleInvoices />,
                },
                {
                  path: '*',
                  element: <NavigateWithRedirect to="eligible" />,
                },
              ],
            },
            {
              path: 'history',
              element: <MarketHistoryLayout />,
              children: [
                {
                  index: true,
                  element: <MarketHistory />,
                },
                {
                  path: '*',
                  element: <NavigateWithRedirect to="." />,
                },
              ],
            },
            {
              path: '*',
              element: <NavigateWithRedirect to="invoices/eligible" />,
            },
          ],
        },
      ],
    },
    // old path redirect
    {
      path: 'markets/search',
      element: <NavigateWithRedirect to={asEnterpriseLink('find-your-customers')} />,
    },
    // old path redirect
    {
      path: 'markets/:marketId/division/:takerId',
      children: [
        { index: true, element: <MarketLayout /> },
        {
          path: '*',
          element: <MarketLayout />,
        },
      ],
    },
    {
      path: 'history',
      element: <ConsolidatedHistoryLayout />,
      children: [
        {
          index: true,
          element: <ConsolidatedHistory />,
        },
        {
          path: '*',
          element: <NavigateWithRedirect to="." />,
        },
      ],
    },
    // old path redirect
    {
      path: 'recurring-rules',
      element: <NavigateWithRedirect to={asEnterpriseLink('automation')} />,
    },
    {
      path: 'automation',
      element: <Automation />,
    },
    {
      path: 'build-offer',
      element: <OfferBuilder />,
      children: [
        {
          index: true,
          element: <CustomerSelect />,
        },
        {
          path: 'frequency',
          element: <FrequencySelect />,
        },
        {
          path: 'rate',
          element: <RateSelectLayout />,
        },
        {
          path: 'preferred-upgrade',
          element: <PreferredRateStep />,
        },
        {
          path: 'review',
          element: <OfferReviewLayout />,
        },
        {
          path: 'confirmation',
          element: <OfferConfirmationLayout />,
        },
      ],
    },
    {
      path: 'offer/acquisition',
      element: (
        <FeatureFlagProtectedRoute featureFlag="enterprise-ui_enableAcquisitionOffer">
          <AcquisitionOfferReview />
        </FeatureFlagProtectedRoute>
      ),
      children: [
        {
          index: true,
          element: <OfferReviewLayout />,
        },
        {
          path: 'confirmation',
          element: <OfferConfirmationLayout />,
        },
      ],
    },
    {
      path: 'offer/:draftOfferUuid',
      element: <DraftOfferReview />,
      children: [
        {
          index: true,
          element: <OfferReviewLayout />,
        },
        {
          path: 'confirmation',
          element: <OfferConfirmationLayout />,
        },
      ],
    },
    {
      path: 'lending',
      element: <Lending />,
    },
    {
      path: 'termloansbyfundbox',
      element: (
        <FeatureFlagProtectedRoute featureFlag="enterprise-ui_enableFundbox">
          <Fundbox />
        </FeatureFlagProtectedRoute>
      ),
    },
    findYourCustomerRouteConfig,
    accountSettingsRouteConfig,
  ],
};

export default enterpriseRouteConfig;
